import { isEqual } from 'lodash';
import { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { type BaseCustomField, type Clip } from '@air/api/types';
import { clipCanOpenInCanvas } from '@air/feature-edit-canvas';

import { PlaceholderAssetGalleryCard } from '~/components/Gallery/PlaceholderAssetGalleryCard';
import { type GalleryViewRender } from '~/components/Gallery/types';
import { useGetPrivateAssetMenuOptionsCallback } from '~/components/PrivateGallery/useGetPrivateAssetMenuOptionsCallback';
import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { DNDGalleryAsset } from '~/components/v2/components/shared/dnd/DNDGalleryAsset';
import { GallerySmartSearchRelevanceIndicator } from '~/components/v2/components/shared/gallery/GallerySmartSearchRelevanceIndicator';
import { QueryParamNames } from '~/constants/search';
import { useCanCreateCanvasInCurrentLocation } from '~/hooks/canvases/useCanCreateCanvasInCurrentLocation';
import { useOpenClipInCanvas } from '~/hooks/canvases/useOpenClipInCanvas';
import { useClipPermissions } from '~/hooks/useClipPermissions';
import { useGetAvailableCustomFields } from '~/hooks/useGetAvailableCustomFields';
import { useGoToPrivateAssetPage } from '~/hooks/useGoToPrivateAssetPage';
import { useOpenAssetInNewTab } from '~/hooks/useOpenAssetInNewTab';
import { usePrivateSelectionMenuOptions } from '~/hooks/usePrivateSelectionMenuOptions';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { AssetModalPanel } from '~/providers/AssetModalPanelContextProvider';
import { usePrivateCustomFieldsContext } from '~/providers/PrivateCustomFieldsProvider';
import { centralizedBoardLibraryIdSelector } from '~/store/centralizedBoard/selectors';
import { hasSelectionAcrossMultipleViewsSelector } from '~/store/selectedItems/selectors';
import { isMobileAgent } from '~/utils/PlatformHelpers';
import { canSeeAssetCustomFields, canViewAssetDiscussions } from '~/utils/permissions/assetPermissions';
import { isPlaceholderClip } from '~/utils/placeholderClip';

export type PrivateGalleryAssetProps = GalleryViewRender<Clip>;

const PrivateGalleryAssetContent = memo(({ data: asset, box, index }: PrivateGalleryAssetProps) => {
  const { showViewSortingRelevance } = usePrivateSearchUIHelpers();
  const { clipPermissions } = useClipPermissions({
    clipId: asset.id,
  });
  const { customFields } = usePrivateCustomFieldsContext();
  const { getSelectionMenuOptions } = usePrivateSelectionMenuOptions();
  const { getAssetMenuOptionsCallback } = useGetPrivateAssetMenuOptionsCallback();
  const _getItemMenuOptions = getAssetMenuOptionsCallback({ item: asset });
  const { goToAssetPage } = useGoToPrivateAssetPage();
  const { openAssetInNewTab } = useOpenAssetInNewTab();
  const { openClipInCanvas } = useOpenClipInCanvas();
  const { canCreateCanvas } = useCanCreateCanvasInCurrentLocation();
  const urlLibraryId = useURLLibraryIdSelector();
  const libraryId = useSelector(centralizedBoardLibraryIdSelector);
  const { getAvailableCustomFields } = useGetAvailableCustomFields<BaseCustomField>({
    libraryId: libraryId || urlLibraryId,
  });
  const hasSelectionAcrossMultipleViews = useSelector(hasSelectionAcrossMultipleViewsSelector);

  const onCustomFieldsClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        query: {
          [QueryParamNames.amTab]: AssetModalPanel.FIELDS,
        },
        trackLocation: 'custom-field-click',
      }),
    [asset, goToAssetPage],
  );

  const onDiscussionsClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        query: {
          [QueryParamNames.amTab]: AssetModalPanel.COMMENTS,
        },
        trackLocation: 'comments-click',
      }),
    [asset, goToAssetPage],
  );

  const onVersionsClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        query: {
          [QueryParamNames.versions]: true,
        },
        trackLocation: 'versions-click',
      }),
    [asset, goToAssetPage],
  );

  const onItemClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        trackLocation: 'card-click',
      }),
    [asset, goToAssetPage],
  );

  const onItemCmdClick = useCallback(
    () =>
      openAssetInNewTab({
        asset,
        trackLocation: 'card-click',
      }),
    [asset, openAssetInNewTab],
  );

  const handleOpenInCanvas = useCallback(() => {
    openClipInCanvas({ action: 'open', clips: [asset], openedFrom: 'asset_card_hover' });
  }, [asset, openClipInCanvas]);

  const canOpenInCanvas = canCreateCanvas && clipCanOpenInCanvas(asset);

  const canViewCustomFields = canSeeAssetCustomFields(clipPermissions);
  const canSeeDiscussions = canViewAssetDiscussions(clipPermissions);

  return (
    <DNDGalleryAsset
      height={box.height}
      width={box.width}
      clip={asset}
      enableDrag={!isMobileAgent && !hasSelectionAcrossMultipleViews}
      index={index}
      isSelectable={true}
      key={asset.id}
      onAssetClick={onItemClick}
      onAssetTitleCmdClick={onItemCmdClick}
      onCustomFieldsClick={onCustomFieldsClick}
      onDiscussionsClick={onDiscussionsClick}
      onOpenInCanvas={canOpenInCanvas ? handleOpenInCanvas : undefined}
      onVersionsClick={onVersionsClick}
      showVersionsIndicator={!!asset.assetVersionCount && asset.assetVersionCount > 1}
      getItemMenuOptions={_getItemMenuOptions}
      getSelectionMenuOptions={getSelectionMenuOptions}
      canViewCustomFields={canViewCustomFields}
      allCustomFields={getAvailableCustomFields(customFields)}
      shouldShowDiscussions={canSeeDiscussions}
      startBadges={showViewSortingRelevance ? <GallerySmartSearchRelevanceIndicator clip={asset} /> : undefined}
    />
  );
}, isEqual);

PrivateGalleryAssetContent.displayName = 'PrivateGalleryAssetContent';

export const PrivateGalleryAsset = memo(
  ({ data: asset, box, index }: PrivateGalleryAssetProps) => {
    if (isPlaceholderClip(asset)) {
      return <PlaceholderAssetGalleryCard clip={asset} cardWidth={box.width} cardHeight={box.height} />;
    }

    return <PrivateGalleryAssetContent data={asset} box={box} index={index} />;
  },
  // `container` comes from `react-virtualized` and is a different object every time but may have the same values so we need to deep equal check the props
  isEqual,
);

PrivateGalleryAsset.displayName = 'PrivateGalleryAsset';
